import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, restoreSession } from "../../services/apiService";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const me = await restoreSession();
        if (!alive) return;

        if (me) {
          navigate("/", { replace: true });
        }
      } catch {
        // ignore restore error
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await loginUser(username, password);
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message;

      if (status === 401) {
        setError(serverMessage || "Invalid username or password.");
      } else if (status === 403) {
        setError(serverMessage || "You are not authorized to access this account.");
      } else if (status === 404) {
        setError(serverMessage || "Login service not found.");
      } else if (status >= 500) {
        setError(serverMessage || "Server error. Please try again later.");
      } else {
        setError(serverMessage || err?.message || "Unable to login. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-overlay" />

      <div className="login-wrapper">
        <section className="login-brand">
          <div className="login-brand-top">
            <div className="login-brand-badge">IMS</div>
            <div className="login-brand-text">
              <span className="login-brand-label">Biocare System</span>
              <h1>Inventory Management System</h1>
            </div>
          </div>

          <p className="login-brand-description">
            Manage stock, monitor transactions, and access your system securely
            through a clean and reliable portal.
          </p>

          <div className="login-brand-panels">
            <div className="brand-panel">
              <span className="brand-panel-title">Secure Access</span>
              <span className="brand-panel-text">
                Protected login for authorized personnel only
              </span>
            </div>

            <div className="brand-panel">
              <span className="brand-panel-title">Operational Control</span>
              <span className="brand-panel-text">
                Centralized access to inventory and workflow tools
              </span>
            </div>
          </div>
        </section>

        <form className="login-card" onSubmit={handleSubmit}>
          <div className="login-card-header">
            <div className="login-chip">Sign in</div>
            <h2>Access your account</h2>
            <p>Enter your username and password to continue</p>
          </div>

          {error && (
            <div className="login-error">
              <strong>Authentication Failed</strong>
              <span>{error}</span>
            </div>
          )}

          <div className="login-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError("");
              }}
              required
              autoComplete="username"
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <div className="login-password-wrap">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? (
              <span className="btn-loading">
                <span className="spinner" />
                Signing in...
              </span>
            ) : (
              "Login"
            )}
          </button>

          <div className="login-footer">
            <span>Biocare IMS Portal</span>
          </div>
        </form>
      </div>
    </div>
  );
}